import {useEffect} from "react"

// CSS variables kept in sync with the slice of the iframe that is currently
// visible inside the parent dashboard's viewport. The theme reads them to keep
// dialogs anchored where the user is actually looking (see PackagingUiProvider).
export const VISIBLE_TOP_VAR = "--pkg-visible-top"
export const VISIBLE_HEIGHT_VAR = "--pkg-visible-height"

/**
 * This app runs as a content-sized iframe: its height is reported to the parent
 * dashboard via postMessage, so the iframe element grows to fit its content and
 * never scrolls itself — the parent page scrolls instead. As a consequence
 * `position: fixed` (used by every MUI Dialog/Modal) is resolved against the
 * iframe's full-height viewport, not against the area the user can actually see,
 * so modals open off-screen and require scrolling up.
 *
 * An IntersectionObserver works across the cross-origin iframe boundary and
 * reports, in the iframe's own coordinate space, which vertical band of the
 * document is on screen. We publish that band as CSS variables on <html>:
 *   --pkg-visible-top:    offset (px) from the iframe top to the visible band
 *   --pkg-visible-height: height (px) of the visible band
 * Dialogs use these to pin themselves near the top of the visible area and to
 * cap their height so they fit within it.
 */
export default function useVisibleViewportVar() {
    useEffect(() => {
        if (typeof IntersectionObserver === "undefined") return

        const root = document.documentElement

        const publish = (top: number, height: number) => {
            root.style.setProperty(VISIBLE_TOP_VAR, `${Math.max(0, Math.round(top))}px`)
            root.style.setProperty(VISIBLE_HEIGHT_VAR, `${Math.max(0, Math.round(height))}px`)
        }

        // Dense thresholds so the band is refreshed frequently while the parent
        // page is scrolled (the observer only fires on threshold crossings).
        const threshold = Array.from({length: 101}, (_, i) => i / 100)

        const observer = new IntersectionObserver((entries) => {
            const entry = entries[entries.length - 1]
            // Ignore frames where the iframe is fully off-screen; keep last value.
            if (entry && entry.intersectionRect.height > 0) {
                publish(entry.intersectionRect.top, entry.intersectionRect.height)
            }
        }, {threshold})

        observer.observe(document.body)
        return () => observer.disconnect()
    }, [])
}
